// Public activity row. Derives the design-card fields from a SubmittedAccountOp
// using the same balance-change / status helpers the legacy summary uses, then
// renders the Home v2 design card (ActivityRow) so public matches private.
import React, { useMemo } from 'react'
import { useModalize } from 'react-native-modalize'

import { Network } from '@ambire-common/interfaces/network'
import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import { getUiType } from '@common/utils/uiType'
import {
  formatBalanceChangeAmount,
  getPresentationalStatus,
  getSummaryBalanceChanges
} from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/helpers'
import SummaryDetailsSheet from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/SummaryDetailsSheet'
import { SubmittedAccountOpLike } from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/types'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'

const { isTab } = getUiType()

import { ActivityKind } from '../tokens'
import useFeeFormatted from '../useFeeFormatted'
import ActivityRow from './ActivityRow'

const LABEL: Record<ActivityKind, string> = {
  swap: 'Swapped',
  send: 'Sent',
  receive: 'Received',
  shield: 'Shielded',
  unshield: 'Unshielded',
  other: 'Transaction'
}

const PublicActivityRow: React.FC<{ op: SubmittedAccountOpLike }> = ({ op }) => {
  const { networks } = useNetworksControllerState()
  const { ref: sheetRef, open: openBottomSheet, close: closeBottomSheet } = useModalize()
  const network = useMemo<Network | undefined>(
    () => networks.find((n: Network) => n.chainId === op.chainId),
    [networks, op.chainId]
  )

  const changes = useMemo(() => getSummaryBalanceChanges(op), [op])
  const nonFee = useMemo(() => changes.filter((c) => c.iconType !== 'gasTank'), [changes])

  const kind: ActivityKind = useMemo(() => {
    // PRIMARY signal: a swap/bridge op always carries `meta.swapTxn` (set by the
    // swapAndBridge controller). Balance-change sign alone misses gasless / CCTP
    // cross-chain swaps, whose source-chain op only shows the outgoing leg and
    // would otherwise be mislabeled "Sent".
    if (op.meta?.swapTxn) return 'swap'
    const hasPos = nonFee.some((c) => c.balanceChange > 0n)
    const hasNeg = nonFee.some((c) => c.balanceChange < 0n)
    if (hasPos && hasNeg) return 'swap'
    if (hasNeg) return 'send'
    if (hasPos) return 'receive'
    return 'other'
  }, [nonFee, op.meta?.swapTxn])

  // For a swap show the token spent (negative leg) so "Swapped -0.1 USDC" reads
  // sensibly even when the received leg lands on another chain (CCTP). Fall back
  // to the first change for non-swaps.
  const primary = useMemo(
    () => (kind === 'swap' && nonFee.some((c) => c.balanceChange < 0n)
      ? nonFee.find((c) => c.balanceChange < 0n)
      : nonFee[0]),
    [kind, nonFee]
  )

  const { feeText, isLoading: feeLoading } = useFeeFormatted(op.gasFeePayment, network)

  if (!network) return null

  return (
    <>
      <ActivityRow
        data={{
          id: op.id || op.txnId || '',
          kind,
          kindLabel: LABEL[kind],
          amount: primary ? formatBalanceChangeAmount(primary) : undefined,
          tokenSymbol: primary?.symbol,
          tokenAddress: primary?.address,
          chainId: op.chainId,
          status: getPresentationalStatus(op) ?? AccountOpStatus.Pending,
          feeText,
          isFeeLoading: feeLoading,
          networkName: network.name,
          timestampMs: op.timestamp || 0,
          explorerUrl: network.explorerUrl,
          txHash: op.txnId,
          onPress: openBottomSheet
        }}
      />
      {/* Tap the row → same humanized detail sheet the legacy popup used. */}
      <SummaryDetailsSheet
        sheetRef={sheetRef}
        closeBottomSheet={closeBottomSheet}
        modalType={isTab ? 'modal' : 'bottom-sheet'}
        submittedAccountOp={op}
        network={network}
        size="md"
        defaultType="full-info"
      />
    </>
  )
}

export default React.memo(PublicActivityRow)
